import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Textarea } from "@/shared/ui/textarea";
import { Checkbox } from "@/shared/ui/checkbox";
import { Badge } from "@/shared/ui/badge";
import { Plus, Trash2, Shield, FileText, GraduationCap, Settings } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchRootCauseByMethod, upsertRootCauseForClaim } from "../api/root-causes";
import { createSupplierPortalClient } from "../api/supplier-portal";
import { toast } from "sonner";

interface PreventRecurrenceProps {
  claimId: string;
  supplierToken?: string;
}

interface PreventionMeasure {
  id: string;
  category: "process" | "training" | "documentation" | "system";
  description: string;
  responsible: string;
  completed: boolean;
}

interface PreventionData {
  measures: PreventionMeasure[];
  standardizationNotes: string;
}

const categoryConfig = {
  process: { icon: Settings, label: "Process Update", color: "bg-chart-2/20 text-chart-2" },
  training: { icon: GraduationCap, label: "Training", color: "bg-success/20 text-success" },
  documentation: { icon: FileText, label: "Documentation", color: "bg-warning/20 text-warning" },
  system: { icon: Shield, label: "System Change", color: "bg-chart-3/20 text-chart-3" },
};

export function PreventRecurrence({ claimId, supplierToken }: PreventRecurrenceProps) {
  const queryClient = useQueryClient();
  const [newMeasure, setNewMeasure] = useState<Partial<PreventionMeasure>>({
    category: "process",
    description: "",
    responsible: "",
  });
  const [notesDraft, setNotesDraft] = useState("");
  const supplierClient = supplierToken ? createSupplierPortalClient(supplierToken) : null;

  const { data: preventionData, isLoading } = useQuery({
    queryKey: ["mod_8d_prevention", claimId],
    queryFn: async () => {
      if (supplierClient) {
        const data = await supplierClient.fetchRootCauses("prevention");
        const rootCause = Array.isArray(data) ? data[0] : data;
        return (rootCause?.data as unknown as PreventionData) || { measures: [], standardizationNotes: "" };
      }
      const data = await fetchRootCauseByMethod(claimId, "prevention");
      return (data?.data as unknown as PreventionData) || { measures: [], standardizationNotes: "" };
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (newData: PreventionData) => {
      if (supplierClient) {
        await supplierClient.upsertRootCause("prevention", JSON.parse(JSON.stringify(newData)));
        return;
      }
      await upsertRootCauseForClaim(claimId, "prevention", JSON.parse(JSON.stringify(newData)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mod_8d_prevention", claimId] });
      toast.success("Prevention measures saved");
    },
    onError: () => {
      toast.error("Failed to save prevention measures");
    },
  });

  useEffect(() => {
    if (!preventionData) return;
    setNotesDraft(preventionData.standardizationNotes || "");
  }, [preventionData?.standardizationNotes]);

  const addMeasure = () => {
    if (!newMeasure.description?.trim()) return;

    const measure: PreventionMeasure = {
      id: crypto.randomUUID(),
      category: newMeasure.category as PreventionMeasure["category"],
      description: newMeasure.description,
      responsible: newMeasure.responsible || "",
      completed: false,
    };

    const updated: PreventionData = {
      measures: [...(preventionData?.measures || []), measure],
      standardizationNotes: preventionData?.standardizationNotes || "",
    };

    saveMutation.mutate(updated);
    setNewMeasure({ category: "process", description: "", responsible: "" });
  };

  const toggleMeasure = (id: string) => {
    if (!preventionData) return;

    const updated: PreventionData = {
      ...preventionData,
      measures: preventionData.measures.map((m) =>
        m.id === id ? { ...m, completed: !m.completed } : m
      ),
    };

    saveMutation.mutate(updated);
  };

  const deleteMeasure = (id: string) => {
    if (!preventionData) return;

    const updated: PreventionData = {
      ...preventionData,
      measures: preventionData.measures.filter((m) => m.id !== id),
    };

    saveMutation.mutate(updated);
  };

  const notesDirty = notesDraft !== (preventionData?.standardizationNotes || "");

  const saveNotes = () => {
    const updated: PreventionData = {
      measures: preventionData?.measures || [],
      standardizationNotes: notesDraft,
    };

    saveMutation.mutate(updated);
  };

  if (isLoading) {
    return <div className="animate-pulse h-32 bg-muted rounded-lg" />;
  }

  const completedCount = preventionData?.measures.filter((m) => m.completed).length || 0;
  const totalCount = preventionData?.measures.length || 0;

  return (
    <div className="space-y-4">
      <Card className="border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              Prevention Measures
            </CardTitle>
            {totalCount > 0 && (
              <Badge variant="outline" className="text-xs">
                {completedCount}/{totalCount} completed
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add new measure */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <select
              value={newMeasure.category}
              onChange={(e) => setNewMeasure({ ...newMeasure, category: e.target.value as PreventionMeasure["category"] })}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="process">Process Update</option>
              <option value="training">Training</option>
              <option value="documentation">Documentation</option>
              <option value="system">System Change</option>
            </select>
            <Input
              placeholder="Description"
              value={newMeasure.description}
              onChange={(e) => setNewMeasure({ ...newMeasure, description: e.target.value })}
              className="md:col-span-2"
            />
            <div className="flex gap-2">
              <Input
                placeholder="Responsible"
                value={newMeasure.responsible}
                onChange={(e) => setNewMeasure({ ...newMeasure, responsible: e.target.value })}
              />
              <Button size="icon" onClick={addMeasure} disabled={!newMeasure.description?.trim()}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Measures list */}
          <div className="space-y-2">
            {preventionData?.measures.map((measure) => {
              const config = categoryConfig[measure.category];
              const Icon = config.icon;

              return (
                <div
                  key={measure.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border border-border ${
                    measure.completed ? "bg-muted/50" : "bg-background"
                  }`}
                >
                  <Checkbox
                    checked={measure.completed}
                    onCheckedChange={() => toggleMeasure(measure.id)}
                  />
                  <Badge className={`${config.color} shrink-0`}>
                    <Icon className="h-3 w-3 mr-1" />
                    {config.label}
                  </Badge>
                  <span className={`flex-1 text-sm ${measure.completed ? "line-through text-muted-foreground" : ""}`}>
                    {measure.description}
                  </span>
                  {measure.responsible && (
                    <span className="text-xs text-muted-foreground">{measure.responsible}</span>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => deleteMeasure(measure.id)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              );
            })}
            {(!preventionData?.measures || preventionData.measures.length === 0) && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No prevention measures added yet
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Standardization Notes */}
      <Card className="border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            Standardization & Horizontal Deployment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder="Document how these measures will be standardized and deployed across similar processes, products, or locations..."
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            className="min-h-[100px]"
          />
          <div className="mt-3 flex justify-end">
            <Button
              size="sm"
              onClick={saveNotes}
              disabled={!notesDirty || saveMutation.isPending}
            >
              {saveMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
